import type { GaVueComponent } from "@/common/vueUtils";
import { GaBadge } from "@/components/general/GaBadge";
import { GaNumberedList } from "@/components/general/GaNumberedList";
import type { Store } from "@/common/storeUtils";
import { GaButton } from "@/components/general/GaButton";
import type { DiscussState } from "@/components/miab/MIABPage";
import type { ContentMetadata_MIAB, MIABPageProps, MIABQuestionMetadata } from "@newgenerated/shared/schema";
import type { TranslationFn } from "@utils/vue-migration/common/gaContext/gaContextTranslations";
import { DateTimeUtils } from "@utils/type/DateTimeUtils";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { GaAlert } from "@/components/general/GaAlert";
import { DateTime } from "luxon";
import { getMiabBaseLink } from "@/components/miab/miabUtils";
import { GaTooltip } from "@/components/general/GaTooltip";
import { EditForm } from "@/components/miab/pageComponents/EditForm";

function EditInformation(props: { t: TranslationFn; username: string; modifiedAt: DateTime }): GaVueComponent {
  const { t, username, modifiedAt } = props;
  const { currentLanguage } = useGaContext();
  return (
    <div class="d-flex gap-2 my-3">
      <div class="h4">
        <i class="ico-info-circle" />
      </div>
      <p>{t("miab:page.discuss.editInfo", [username, DateTimeUtils.formatWithLocale(modifiedAt.toLocal(), currentLanguage())])}</p>
    </div>
  );
}

function getMiabEditLink(link: ContentMetadata_MIAB): string | undefined {
  const miabEditLink = getMiabBaseLink(link);
  miabEditLink.searchParams.set("openEdit", "true");
  miabEditLink.hash = "discussSection";
  return miabEditLink.href;
}

export function Discuss(props: { state: Store<DiscussState>; questions: Store<MIABQuestionMetadata[]>; page: MIABPageProps; t: TranslationFn }): GaVueComponent {
  const t = props.t;
  const state = props.state.unpackUnion();
  const { customer } = useGaContext();
  const { miab, linkedMiab } = props.page;

  return (
    <>
      <div class="mb-2">
        <GaBadge variant="orange-light">{t("miab:page.discuss.badge")?.toUpperCase()}</GaBadge>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <h2>{t("miab:page.discuss.title")}</h2>
        {customer !== null ? (
          <>
            {state.kind === "VIEW" && (linkedMiab === null || (miab.userContext !== null && customer.customerId === miab.userContext.customerId)) ? (
              <div>
                <GaButton
                  variant={"primary-alt"}
                  onClick={() =>
                    props.state.set({ errors: { general: [], fields: { additionalProperties: {} } }, modifiedQuestions: [...props.questions.get()], kind: "EDIT", aiTool: { kind: "OPEN", status: "IDLE", prompt: "", madeARequest: false } })
                  }>
                  <i class="ico-sparks-2" /> {t("miab:edit.form.create")}
                </GaButton>
                &nbsp;
                <GaButton variant={"primary-alt"} onClick={() => props.state.set({ errors: { general: [], fields: { additionalProperties: {} } }, modifiedQuestions: [...props.questions.get()], kind: "EDIT", aiTool: { kind: "CLOSED" } })}>
                  {t("miab:edit.form.edit")}
                </GaButton>
              </div>
            ) : null}
            {state.kind === "VIEW" && miab.userContext === null && linkedMiab !== null ? (
              <div>
                <GaButton variant={"primary-alt"} href={getMiabEditLink(linkedMiab)}>
                  {t("miab:edit.form.edit")}
                </GaButton>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      {state.kind === "EDIT" ? (
        <p>
          {state.store.get().aiTool.kind === "OPEN" ? (
            <>
              <strong>{props.t("miab:edit.form.aiInfo")}</strong>
              <GaTooltip title={`<div class="text-start">${props.t("miab:edit.form.aiInfo.extended")}</div>`} htmlEnabled={true}>
                <i class="ico-info-circle" />
              </GaTooltip>
            </>
          ) : (
            <>
              <strong>{props.t("miab:edit.form.info")}</strong>
              <GaTooltip title={`<div class="text-start">${props.t("miab:edit.form.info.extended")}</div>`} htmlEnabled={true}>
                <i class="ico-info-circle" />
              </GaTooltip>
            </>
          )}
        </p>
      ) : (
        <>
          <p>
            <strong>{t("miab:page.discuss.description")}</strong>
          </p>
          {miab.userContext !== null ? <EditInformation t={t} modifiedAt={state.store.get().modifiedDate} username={miab.userContext.username} /> : null}
        </>
      )}
      <div class="miab-questions">
        {state.kind === "VIEW" ? (
          <>
            <GaNumberedList items={[...props.questions.get()].sort((a, b) => a.sortOrder - b.sortOrder).map((question) => ({ title: question.title, text: question.question }))} />
            {linkedMiab !== null && linkedMiab.common.link !== null && (props.page.miab.userContext === null || (customer !== null && customer.customerId === props.page.miab.userContext.customerId)) ? (
              <div class="mt-4">
                <GaAlert variant={"info"}>
                  <p vHtml={miab.userContext === null ? t("miab:page.linkedMiab.original", [linkedMiab.common.link]) : t("miab:page.linkedMiab.edited", [linkedMiab.common.link])}></p>
                </GaAlert>
              </div>
            ) : null}
          </>
        ) : (
          <EditForm state={state.store} t={props.t} miab={props.page.miab} questionStore={props.questions} changeToEdit={() => props.state.set({ kind: "VIEW", modifiedDate: DateTime.now() })} />
        )}
      </div>
    </>
  );
}
